import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';

/**
 * Event type to Bootstrap icon mapping
 * Maps the 26 organization activity event types to appropriate icons
 */
const EVENT_TYPE_ICONS: Record<string, string> = {
  'flight.created': 'bi-airplane',
  'flight.started': 'bi-airplane',
  'flight.completed': 'bi-airplane',
  'drone.created': 'bi-box-seam',
  'drone.status_changed': 'bi-box-seam',
  'drone.assigned': 'bi-box-seam',
  'team.member_joined': 'bi-people',
  'customer.created': 'bi-person',
  'field.created': 'bi-geo-alt',
  'custom_chemical.created': 'bi-droplet',
  'chemical_template.created': 'bi-droplet',
  'report.generated': 'bi-file-text',
  'safety_test.completed': 'bi-clipboard-check',
  'safety_test.due': 'bi-clipboard-check',
  'ticket.created': 'bi-life-preserver',
  'ticket.closed': 'bi-life-preserver',
  'ticket.reopened': 'bi-life-preserver',
  'ticket.reply': 'bi-life-preserver',
  'appointment.requested': 'bi-calendar-event',
  'appointment.cancelled': 'bi-calendar-event',
  'appointment.scheduled': 'bi-calendar-event',
  'appointment.completed': 'bi-calendar-event',
  'work_order.created': 'bi-clipboard2',
  'work_order.in_progress': 'bi-clipboard2',
  'work_order.completed': 'bi-clipboard2',
  'work_order.cancelled': 'bi-clipboard2',
  'certification.uploaded': 'bi-patch-check',
  'certification.renewed': 'bi-patch-check',
};

/**
 * Event type category to badge color class mapping
 */
const EVENT_TYPE_BADGE_CLASS: Record<string, string> = {
  'flight': 'bg-primary',
  'drone': 'bg-info',
  'team': 'bg-success',
  'customer': 'bg-warning',
  'field': 'bg-secondary',
  'custom_chemical': 'bg-danger',
  'chemical_template': 'bg-danger',
  'report': 'bg-dark',
  'safety_test': 'bg-warning',
  'ticket': 'bg-info',
  'appointment': 'bg-primary',
  'work_order': 'bg-secondary',
  'certification': 'bg-success',
};

interface ActivityResponse {
  success: boolean;
  data: {
    activities: any[];
    nextCursor: string | null;
    hasMore: boolean;
  };
}

/**
 * Organization Activity Tab Component
 *
 * Displays a chronological activity feed for an organization.
 * Used within the admin organization detail page.
 * Supports cursor-based pagination with "Load More" button.
 *
 * Reference: /agent-os/specs/2026-02-17-admin-dashboard-flight-records/spec.md
 */
@Component({
  selector: 'app-organization-activity-tab',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './organization-activity-tab.component.html',
  styleUrl: './organization-activity-tab.component.scss'
})
export class OrganizationActivityTabComponent implements OnInit {
  @Input() organizationId!: string;

  activities: any[] = [];
  nextCursor: string | null = null;
  hasMore = false;
  loading = true;
  loadingMore = false;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    this.loadActivities();
  }

  /**
   * Load activities from the admin API endpoint.
   * When a cursor is provided, appends results to existing activities (pagination).
   * When no cursor is provided, replaces the activities array (initial load).
   */
  loadActivities(cursor?: string): void {
    if (cursor) {
      this.loadingMore = true;
    } else {
      this.loading = true;
    }

    let url = `${environment.apiUrl}/admin/organizations/${this.organizationId}/activities?limit=20`;
    if (cursor) {
      url += `&cursor=${encodeURIComponent(cursor)}`;
    }

    this.http.get<ActivityResponse>(url).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          if (cursor) {
            // Append to existing activities for "Load More"
            this.activities = [...this.activities, ...response.data.activities];
          } else {
            // Replace activities for initial load
            this.activities = response.data.activities;
          }
          this.nextCursor = response.data.nextCursor;
          this.hasMore = response.data.hasMore;
        }
        this.loading = false;
        this.loadingMore = false;
      },
      error: (error) => {
        console.error('Failed to load activities:', error);
        this.loading = false;
        this.loadingMore = false;
      }
    });
  }

  /**
   * Get the Bootstrap icon class for a given event type
   */
  getEventIcon(eventType: string): string {
    return EVENT_TYPE_ICONS[eventType] || 'bi-activity';
  }

  /**
   * Get the badge color class for a given event type
   */
  getEventBadgeClass(eventType: string): string {
    const category = eventType.split('.')[0];
    return EVENT_TYPE_BADGE_CLASS[category] || 'bg-secondary';
  }

  /**
   * Get a human-readable label for the event type
   */
  getEventLabel(eventType: string): string {
    const parts = eventType.split('.');
    const category = parts[0].replace(/_/g, ' ');
    const action = parts[1]?.replace(/_/g, ' ') || '';
    return `${category} ${action}`.trim();
  }

  /**
   * Calculate a relative time string from an ISO date string.
   * Returns values like "2 hours ago", "3 days ago", etc.
   */
  getRelativeTime(dateString: string): string {
    if (!dateString) return '';

    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffWeeks = Math.floor(diffDays / 7);
    const diffMonths = Math.floor(diffDays / 30);

    if (diffMinutes < 1) {
      return 'Just now';
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else if (diffWeeks < 5) {
      return `${diffWeeks} week${diffWeeks !== 1 ? 's' : ''} ago`;
    } else if (diffMonths < 12) {
      return `${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
}
